import cn from 'classnames';
import React, { FC, forwardRef, ReactNode, useContext, useRef } from 'react';
import { AnswerState } from 'schema/src/types/state.generated';
import Markdown from '../../../../components/Markdown';
import { RootContext } from '../../../../contexts/rootContext';
import { inIframe } from '../../../../utils/location';
import { ContentWrapper } from '../ContentWrapper';
import { useMatrixContext } from '../MatrixWrapper/MatrixWrapper';
import styles from './MatrixQuestionWrapper.module.css';

export type MatrixQuestionWrapperProps = {
  question: { text: string; type: string };
  children: ReactNode;
  questionId: string;
  isFirstPollQuestion: boolean;
  isLast: boolean;
  withoutTitle: boolean;
  error: boolean;
  answerState?: AnswerState;
};

export const MatrixQuestionWrapper: FC<MatrixQuestionWrapperProps> = ({
  children,
  questionId,
  question,
  isFirstPollQuestion,
  isLast,
  withoutTitle,
  error,
}) => {
  const { viewMode } = useMatrixContext();
  const { schemaEngine } = useContext(RootContext);

  const ref = useRef<HTMLDivElement>(null);

  const isRating = schemaEngine.hasTrait(question.type, 'RATING_TYPE');

  const matrixFieldProps = {
    ref,
    questionId,
    className: cn(styles.matrixTitle, {
      [c(styles.matrixTitleCompact)]: ['question', 'compact'].includes(viewMode),
      [c(styles.firstRow)]: viewMode === 'default' && isFirstPollQuestion,
      [c(styles.ratingTitle)]: viewMode === 'default' && isRating,
    }),
    ...(!isRating &&
      viewMode === 'default' && {
        offset: 'start' as const,
      }),
  };

  if (viewMode === 'question' || viewMode === 'compact') {
    const { divider, borders } = (() => {
      if (isFirstPollQuestion && isLast) {
        return { divider: 'none', borders: 'all' } as const;
      }

      if (isFirstPollQuestion && error) {
        return { divider: 'top', borders: 'none' } as const;
      }

      if (isFirstPollQuestion && withoutTitle) {
        return { divider: 'none', borders: 'top' } as const;
      }

      return { divider: 'top', borders: isLast ? 'bottom' : 'none' } as const;
    })();

    return (
      <ContentWrapper divider={divider} borders={borders}>
        <div className={cn({ [c(styles.withSpacing)]: inIframe() })}>
          <MatrixFieldWrapper {...matrixFieldProps}>
            <Markdown>{question.text}</Markdown>
          </MatrixFieldWrapper>

          {children}
        </div>
      </ContentWrapper>
    );
  }

  return (
    <>
      <MatrixFieldWrapper {...matrixFieldProps}>
        <Markdown>{question.text}</Markdown>
      </MatrixFieldWrapper>
      {children}
    </>
  );
};

interface MatrixFieldWrapperProps {
  className?: string;
  questionId: string;
  offset?: 'start' | 'end';
}

export const MatrixFieldWrapper = forwardRef<HTMLDivElement, React.PropsWithChildren<MatrixFieldWrapperProps>>(
  function MatrixFiledWrapper({ children, className, questionId, offset }, ref) {
    const { viewMode, questionType } = useMatrixContext();
    const isRating = ['MATRIX_POLL_RATING_STARS', 'MATRIX_POLL_RATING_DIGITS'].includes(questionType);

    const mouseEnterHandler = () => {
      if (viewMode === 'default' && !isRating) {
        const rowQuestion = document.querySelectorAll<HTMLDivElement>(`div[data-matrix-column='${questionId}']`);
        const firstElementInRow = rowQuestion[0];
        const lastElemInRow = rowQuestion[rowQuestion.length - 1];

        if (rowQuestion.length) {
          rowQuestion.forEach(el => (el.style.background = 'var(--tui-background-base-alt)'));
        }

        if (firstElementInRow) {
          firstElementInRow.style.borderRadius =
            'var(--custom-survey-elements-border-radius) 0 0 var(--custom-survey-elements-border-radius)';
        }

        if (lastElemInRow) {
          lastElemInRow.style.borderRadius =
            '0 var(--custom-survey-elements-border-radius) var(--custom-survey-elements-border-radius) 0';
        }
      }
    };

    const mouseLeaveHandler = () => {
      if (viewMode === 'default' && !isRating) {
        const rowQuestion = document.querySelectorAll<HTMLDivElement>(`div[data-matrix-column='${questionId}']`);
        const firstElementInRow = rowQuestion[0];
        const lastElementInRow = rowQuestion[rowQuestion.length - 1];

        if (rowQuestion.length) {
          rowQuestion.forEach(el => (el.style.background = ''));
        }

        if (firstElementInRow) {
          firstElementInRow.style.borderRadius = '';
        }

        if (lastElementInRow) {
          lastElementInRow.style.borderRadius = '';
        }
      }
    };

    return (
      <div
        ref={ref}
        className={cn(className, {
          [c(styles.offsetStart)]: offset === 'start',
          [c(styles.offsetEnd)]: offset === 'end',
          [c(styles.withoutLeftSpacing)]: isRating,
        })}
        data-matrix-column={questionId}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
      >
        {children}
      </div>
    );
  },
);
